import React, { useState, useEffect } from 'react';
import { Database, Trash2, RefreshCw, AlertTriangle, PlayCircle, Sparkles } from 'lucide-react';
import { api } from '../../services/api';
import { useNotifications } from '../NotificationContext';

interface DataManagementSettingsProps {
  onSave?: () => void;
}

const DataManagementSettings: React.FC<DataManagementSettingsProps> = ({ onSave }) => {
  const { addNotification } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [systemStatus, setSystemStatus] = useState({
    mode: 'demo',
    isLive: false,
    totalCustomers: 0,
    totalSubscriptions: 0,
    totalInvoices: 0,
    hasDemoData: true,
  });

  const loadStatus = async () => {
    try {
      const res = await api.system.getMode();
      setSystemStatus(res);
    } catch {
      // Keep state
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleClearDemoData = async (wipeAll: boolean = false) => {
    setLoading(true);
    try {
      const res = await api.system.clearDemoData({ wipeAll });
      addNotification({
        type: 'success',
        title: wipeAll ? 'Clean Slate Active' : 'Demo Data Cleared',
        message: res.message || 'Sample test data cleared. Dashboard values reset to zero.',
      });
      await loadStatus();
      if (onSave) onSave();
      window.dispatchEvent(new CustomEvent('subscale_data_reset'));
    } catch {
      addNotification({
        type: 'error',
        title: 'Operation Failed',
        message: 'Could not clear demo records. Check server connection.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLoadDemoData = async () => {
    setLoading(true);
    try {
      const res = await api.system.loadDemoData();
      addNotification({
        type: 'success',
        title: 'Demo Dataset Loaded',
        message: res.message || 'Sample demo data loaded for platform testing.',
      });
      await loadStatus();
      if (onSave) onSave();
      window.dispatchEvent(new CustomEvent('subscale_data_reset'));
    } catch {
      addNotification({
        type: 'error',
        title: 'Load Failed',
        message: 'Failed to load sample dataset.',
      });
    } finally {
      setLoading(false);
    }
  };

  const isLiveClean = systemStatus.mode === 'live' && !systemStatus.hasDemoData;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm p-6 space-y-6 animate-in slide-in-from-left-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-700 gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
            <Database className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">Data Management & Environment</h2>
            <p className="text-xs text-slate-500 font-medium">Control sample datasets, production resets, and environment metrics.</p>
          </div>
        </div>

        <span className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 ${
          isLiveClean
            ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800'
            : 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
        }`}>
          <span className={`w-2 h-2 rounded-full ${isLiveClean ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'}`} />
          {isLiveClean ? 'Live Production (Clean)' : 'Demo Mode (Sample Data)'}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Total Customers</p>
          <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">{systemStatus.totalCustomers}</p>
        </div>
        <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Active Subscriptions</p>
          <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">{systemStatus.totalSubscriptions}</p>
        </div>
        <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700">
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Issued Invoices</p>
          <p className="text-2xl font-black text-slate-900 dark:text-white mt-1">{systemStatus.totalInvoices}</p>
        </div>
      </div>

      <div className="space-y-4 pt-2">
        <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              <h3 className="text-sm font-black text-slate-900 dark:text-white">Clear Demo Data / Start Fresh</h3>
            </div>
            <p className="text-xs text-slate-500">
              Removes all sample demo customers, subscriptions, and test metrics (like default LTV and recovered revenue). Resets your dashboard to pure zero state.
            </p>
          </div>
          <button
            onClick={() => handleClearDemoData(false)}
            disabled={loading}
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider rounded-xl transition-all shadow-xs flex items-center justify-center gap-2 whitespace-nowrap"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Clear Demo Data
          </button>
        </div>

        <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-700 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <h3 className="text-sm font-black text-slate-900 dark:text-white">Load Sample Demo Data</h3>
            </div>
            <p className="text-xs text-slate-500">
              Populate realistic test records (5 customers, subscriptions, invoices, and analytics) to explore features.
            </p>
          </div>
          <button
            onClick={handleLoadDemoData}
            disabled={loading}
            className="px-5 py-2.5 bg-white dark:bg-slate-750 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs font-bold uppercase tracking-wider rounded-xl transition-all shadow-xs flex items-center justify-center gap-2 whitespace-nowrap"
          >
            <PlayCircle className="w-3.5 h-3.5 text-emerald-500" />
            Load Demo Data
          </button>
        </div>

        <div className="p-5 rounded-2xl bg-red-50/50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/40 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <h3 className="text-sm font-black text-red-900 dark:text-red-300">Wipe All Records (Zero Slate)</h3>
            </div>
            <p className="text-xs text-red-700/80 dark:text-red-400">
              Permanently wipes all customers, carts, logs, and invoices for a completely clean production launch.
            </p>
          </div>
          <button
            onClick={() => {
              if (window.confirm('Are you sure you want to wipe all records for a clean zero state?')) {
                handleClearDemoData(true);
              }
            }}
            disabled={loading}
            className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider rounded-xl transition-all shadow-xs flex items-center justify-center gap-2 whitespace-nowrap"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Wipe Everything
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataManagementSettings;
